import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import api from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getSupportWhatsAppLink } from '../components/SupportModal';

import {
  LuCheckCircle,
  LuAlertCircle,
  LuLoader,
  LuUser,
  LuCreditCard,
  LuCopy,
  LuKey,
  LuSmartphone,
  LuCheck,
  LuClock,
  LuAlertTriangle,
  LuArrowLeft,
  LuLink,
  LuMail,
} from 'react-icons/lu';

// Ícone do WhatsApp (marca, sem equivalente no Lucide)
import { FaWhatsapp } from 'react-icons/fa';

// Animações
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 40px 20px;
  flex: 1;
  animation: ${fadeIn} 0.5s ease;
  
  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const PageTitle = styled.div`
  text-align: center;
  margin-bottom: 30px;
  
  h1 {
    font-size: 28px;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 8px;
  }
  
  p {
    font-size: 16px;
    color: ${props => props.theme.colors.grayMedium};
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 24px;
    }
    p {
      font-size: 14px;
    }
  }
`;

const Card = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.card};
  overflow: hidden;
  margin-bottom: 20px;
`;

// Tom do ícone por status (fundo suave + ícone na cor de acento).
// Erro usa âmbar (não vermelho alarmista): é recuperável, o atendimento retorna.
const toneOf = (props) => {
  const c = props.theme.colors;
  if (props.$status === 'success') return { fg: c.success, bg: '#ecfdf5' };
  if (props.$status === 'error') return { fg: c.warning, bg: '#fffbeb' };
  return { fg: c.primary, bg: c.lightBlue }; // loading / default
};

const CardHeader = styled.div`
  background: ${props => props.theme.colors.white};
  padding: 40px 32px 24px;
  text-align: center;
`;

const IconWrapper = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => toneOf(props).fg};
  background: ${props => toneOf(props).bg};

  svg { width: 34px; height: 34px; }

  ${props => props.$loading && css`
    background: none;
    animation: ${spin} 1.2s linear infinite;
  `}
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 6px;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
`;

const CardBody = styled.div`
  padding: 30px;
  padding-top: 10px;
`;

const InfoSection = styled.div`
  margin-bottom: 25px;
`;

const InfoTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid ${props => props.theme.colors.lightBlue};
  display: flex;
  align-items: center;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px;
  background: ${props => props.$highlight ? '#f0fdf4' : props.theme.colors.grayLight};
  border-radius: 8px;
  margin-bottom: 10px;
  border-left: ${props => props.$highlight ? `4px solid ${props.theme.colors.successGreen}` : 'none'};
`;

const InfoLabel = styled.span`
  color: ${props => props.theme.colors.grayMedium};
  font-size: 14px;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: ${props => props.theme.colors.grayDark};
  font-size: 14px;
  font-weight: 600;
`;

const Alert = styled.div`
  background: ${props => props.$type === 'success' ? '#d1fae5' : props.$type === 'warning' ? '#fef3c7' : '#fee2e2'};
  color: ${props => props.$type === 'success' ? '#065f46' : props.$type === 'warning' ? '#92400e' : '#991b1b'};
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.6;
  border-left: 4px solid ${props => props.$type === 'success' ? props.theme.colors.successGreen : props.$type === 'warning' ? '#f59e0b' : props.theme.colors.errorRed};
  
  strong {
    font-weight: 700;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background: ${props => props.$variant === 'secondary'
    ? props.theme.colors.grayMedium
    : props.theme.colors.successGreen
  };
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-bottom: ${props => props.$mb || '0'};

  &:hover:not(:disabled) {
    background: ${props => props.$variant === 'secondary' ? props.theme.colors.grayDark : '#059669'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const WhatsAppSupportLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 16px;
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #10b981;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  margin-bottom: 12px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #d1fae5;
  }
`;

const PixSection = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 25px;
  margin-top: 0px;
`;

const PixTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #065f46;
  margin-bottom: 15px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QRCode = styled.img`
  display: block;
  margin: 0 auto 15px;
  max-width: 220px;
  width: 100%;
  border-radius: 12px;
  border: 3px solid white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const PixCodeBox = styled.div`
  background: white;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  padding: 12px;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  word-break: break-all;
  color: #334155;
  margin-bottom: 12px;
  max-height: 80px;
  overflow-y: auto;
`;

const CopyButton = styled.button`
  width: 100%;
  padding: 12px;
  background: ${props => props.theme.colors.successGreen};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #059669;
  }
`;

const PaymentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 25px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CardPaymentSection = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CardPaymentTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
`;

const CardPaymentText = styled.p`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 16px;
`;

const CardPaymentButton = styled.button`
  width: 100%;
  padding: 12px;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.2s ease;
  margin-top: auto;

  &:hover:not(:disabled) {
    background: #1e40af;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const PaymentIntroText = styled.p`
  text-align: center;
  font-size: 14px;
  color: #4b5563;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const MobilePaymentSelector = styled.div`
  display: none;
  margin-top: 12px;
  margin-bottom: 8px;
  justify-content: center;
  gap: 10px;

  @media (max-width: 768px) {
    display: flex;
    flex-wrap: wrap;
  }
`;

const MobilePaymentButton = styled.button`
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid ${props => (props.$active ? '#111827' : '#e5e7eb')};
  background: ${props => (props.$active ? '#111827' : '#ffffff')};
  color: ${props => (props.$active ? '#ffffff' : '#111827')};
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.$active ? '#111827' : '#f3f4f6')};
  }
`;

const PortalSection = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 25px;
  margin-top: 25px;
`;

const PortalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PortalInfo = styled.div`
  background: white;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 12px;
`;

const PortalLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
  font-weight: 500;
`;

const PortalValue = styled.div`
  font-size: 16px;
  color: ${props => props.theme.colors.primary};
  font-weight: 700;
  font-family: 'Courier New', monospace;
`;

const PortalLink = styled.a`
  display: block;
  text-align: center;
  padding: 14px;
  background: ${props => props.theme.colors.primary};
  color: white;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: background-color 0.2s ease;

  &:hover {
    color: white;
    background: ${props => props.theme.colors.hoverPrimary};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px;
  border: 2px solid ${props => props.$error ? props.theme.colors.errorRed : '#e2e8f0'};
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.$error ? props.theme.colors.errorRed : props.theme.colors.primary};
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const ErrorText = styled.p`
  color: ${props => props.theme.colors.errorRed};
  font-size: 13px;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const SpinningIcon = styled(LuLoader)`
  display: inline;
  vertical-align: middle;
  margin-right: 8px;
  animation: ${spin} 1s linear infinite;
`;

const ConfirmacaoCadastro = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('loading');
  const [cadastroData, setCadastroData] = useState(null);
  const [dadosCompletos, setDadosCompletos] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [cpf, setCpf] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [isGeneratingCardLink, setIsGeneratingCardLink] = useState(false);
  const [cardLinkError, setCardLinkError] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('pix');
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setStatus('error');
      setErrorMessage('Token não fornecido na URL.');
    }
  }, [token]);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth <= 768);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const validateToken = async () => {
    try {
      setStatus('loading');
      
      // Validar o token
      const validacaoResponse = await api.get(`/confirmacao/validar/${token}`);
      
      if (validacaoResponse.data.success) {
        setCadastroData(validacaoResponse.data.data);
        // Ir para tela de validação de CPF
        setStatus('awaiting_cpf');
      }
    } catch (error) {
      console.error('Erro ao validar token:', error);
      
      if (error.response?.data?.contratoConfirmadoError) {
        setStatus('error');
        setErrorMessage(
          error.response.data.message ||
            'Infelizmente estamos com problemas para finalizar seu contrato. Em breve um atendente entrará em contato para que consiga resolver o problema.'
        );
      } else if (error.response?.data?.alreadyConfirmed) {
        // Link já foi usado - mostrar mensagem especial
        setStatus('already_used');
        setContactInfo(error.response.data.contactInfo);
        setErrorMessage(error.response.data.message || 'Este link já foi utilizado.');
      } else if (error.response?.data?.expired) {
        setStatus('error');
        setErrorMessage('Este link de confirmação expirou. Entre em contato conosco.');
      } else if (!error.response) {
        setStatus('error');
        setErrorMessage('Não foi possível validar o link. Tente novamente mais tarde.');
      } else {
        setStatus('error');
        setErrorMessage(error.response?.data?.message || 'Não foi possível validar o link.');
      }
    }
  };

  const handleFinalizarCadastro = async () => {
    try {
      // Validar CPF antes de enviar
      const cpfLimpo = cpf.replace(/\D/g, '');
      
      if (!cpfLimpo || cpfLimpo.length !== 11) {
        setCpfError('Por favor, informe um CPF válido com 11 dígitos.');
        return;
      }
      
      setCpfError('');
      setStatus('finalizing');
      
      // ETAPA 1: Confirmar meio de contato com validação de CPF
      await api.post(`/confirmacao/confirmar/${token}`, { cpf: cpfLimpo });
      
      // ETAPA 2: Finalizar cadastro imediatamente (sem tela intermediária)
      const response = await api.post(`/confirmacao/finalizar/${token}`);
      
      if (response.data.success) {
        setDadosCompletos(response.data.data);
        setStatus('success');
      }
    } catch (error) {
      console.error('Erro ao finalizar cadastro:', error);
      
      if (error.response?.data?.invalidCPF) {
        setCpfError('CPF inválido. Por favor, informe o CPF do titular da contratação.');
        setStatus('awaiting_cpf');
      } else if (error.response?.data?.contratoConfirmadoError) {
        setStatus('error');
        setErrorMessage(
          error.response.data.message ||
            'Infelizmente estamos com problemas para finalizar seu contrato. Em breve um atendente entrará em contato para que consiga resolver o problema.'
        );
      } else if (error.response?.data?.alreadyConfirmed) {
        setStatus('already_used');
        setContactInfo(error.response.data.contactInfo);
        setErrorMessage(error.response.data.message || 'Este link já foi utilizado.');
      } else if (error.response?.data?.expired) {
        setStatus('error');
        setErrorMessage('Este link de confirmação expirou. Entre em contato conosco.');
      } else if (!error.response) {
        setStatus('error');
        setErrorMessage('Não foi possível finalizar o cadastro. Tente novamente mais tarde.');
      } else {
        setStatus('error');
        setErrorMessage(error.response?.data?.message || 'Não foi possível finalizar o cadastro. Tente novamente mais tarde.');
      }
    }
  };

  const copyPixCode = () => {
    if (dadosCompletos?.pix?.copiaCola) {
      navigator.clipboard.writeText(dadosCompletos.pix.copiaCola);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    }
  };

  const handlePagarComCartao = async () => {
    try {
      setCardLinkError('');
      setIsGeneratingCardLink(true);

      const response = await api.post(`/confirmacao/pagamento/cartao/${token}`);
      const url =
        response.data?.url ||
        response.data?.data?.url ||
        null;

      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setCardLinkError('Não foi possível gerar o link de pagamento. Tente novamente em alguns instantes.');
      }
    } catch (error) {
      console.error('Erro ao gerar link de pagamento com cartão:', error);
      setCardLinkError(
        error.response?.data?.message ||
          'Ocorreu um erro ao gerar o link de pagamento. Tente novamente em alguns instantes.'
      );
    } finally {
      setIsGeneratingCardLink(false);
    }
  };

  const formatCPF = (value) => {
    const numericValue = value.replace(/\D/g, '');
    if (numericValue.length <= 11) {
      return numericValue
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return value;
  };

  const handleCPFChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
    if (cpfError) setCpfError(''); // Limpar erro ao digitar
  };

  // ESTADO: LOADING
  if (status === 'loading') {
    return (
      <PageWrapper>
        <Header />
        <Container>
          <PageTitle>
            <h1>Confirmação de Cadastro</h1>
          </PageTitle>
          <Card>
            <CardHeader $status="loading">
              <IconWrapper $loading $status="loading">
                <LuLoader size={32} />
              </IconWrapper>
              <Title>Validando seu link</Title>
              <Subtitle>Só um instante enquanto confirmamos seus dados.</Subtitle>
            </CardHeader>
          </Card>
        </Container>
        <Footer />
      </PageWrapper>
    );
  }

  // ESTADO: LINK JÁ USADO
  if (status === 'already_used') {
    const getMeioContato = () => {
      if (!contactInfo) return (
        <span>seu meio de contato</span>
      );
      
      if (contactInfo.contactType === 'whatsapp' && contactInfo.telefone) {
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'center' }}>
            <FaWhatsapp size={24} color="#25D366" />
            <span>WhatsApp {contactInfo.telefone}</span>
          </span>
        );
      }
      
      if (contactInfo.email) {
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'center' }}>
            <LuMail size={24} color="#1e3a8a" />
            <span>E-mail {contactInfo.email}</span>
          </span>
        );
      }
      
      return (
        <span>seu meio de contato</span>
      );
    };

    return (
      <PageWrapper>
        <Header />
        <Container>
          <PageTitle>
            <h1>Confirmação de Cadastro</h1>
          </PageTitle>
          <Card>
            <CardHeader $status="success">
              <IconWrapper $status="success">
                <LuCheckCircle size={48} />
              </IconWrapper>
              <Title>Cadastro já finalizado</Title>
              <Subtitle>Este link já foi usado para concluir seu cadastro.</Subtitle>
            </CardHeader>
            <CardBody>
              <InfoSection>
                <InfoTitle>
                  Acesso às Credenciais
                </InfoTitle>
                <InfoItem>
                  <div style={{ width: '100%', textAlign: 'center' }}>
                    <InfoLabel style={{ marginBottom: '12px', display: 'block', textAlign: 'center' }}>
                      Suas credenciais de acesso ao portal já foram enviadas para:
                    </InfoLabel>
                    <InfoValue style={{ fontSize: '16px', color: '#059669', display: 'flex', justifyContent: 'center' }}>
                      {getMeioContato()}
                    </InfoValue>
                  </div>
                </InfoItem>
              </InfoSection>

              <PortalSection style={{ marginTop: '20px' }}>
                <PortalTitle>
                  <LuKey style={{ marginRight: '8px' }} /> Portal do Cliente
                </PortalTitle>
                <p style={{ fontSize: '14px', color: '#334155', marginBottom: '15px', textAlign: 'center' }}>
                  Acesse o portal com suas credenciais:
                </p>
                <PortalLink href="https://portal.santacasacopacabana.com.br/" target="_blank" rel="noopener noreferrer">
                  <LuLink style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} size={18} />
                  Acessar Portal
                </PortalLink>
                
                {/* Nota sobre credenciais */}
                <div style={{ 
                  marginTop: '20px',
                  padding: '12px 16px',
                  background: '#f1f5f9',
                  borderLeft: '3px solid #64748b',
                  borderRadius: '6px',
                  textAlign: 'left'
                }}>
                  <p style={{ 
                    fontSize: '13px', 
                    color: '#475569',
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    <LuAlertCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />
                    <strong style={{ fontWeight: '600' }}>Não recebeu as credenciais?</strong><br />
                    <span style={{ paddingLeft: '20px', display: 'inline-block', marginTop: '4px' }}>
                      Verifique sua caixa de spam ou entre em contato com nosso suporte pelos canais disponíveis em nosso site.
                    </span>
                  </p>
                </div>
              </PortalSection>

              <Button onClick={() => navigate('/')} $variant="secondary" style={{ marginTop: '20px' }}>
                <LuArrowLeft style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} size={18} />
                Voltar para Home
              </Button>
            </CardBody>
          </Card>
        </Container>
        <Footer />
      </PageWrapper>
    );
  }

  // ESTADO: ERROR
  if (status === 'error') {
    return (
      <PageWrapper>
        <Header />
        <Container>
          <PageTitle>
            <h1>Confirmação de Cadastro</h1>
          </PageTitle>
          <Card>
            <CardHeader $status="error">
              <IconWrapper $status="error">
                <LuAlertTriangle size={48} />
              </IconWrapper>
              <Title>Algo deu errado ao finalizar</Title>
              <Subtitle>Não conseguimos concluir seu cadastro agora.</Subtitle>
            </CardHeader>
            <CardBody>
              <Alert $type="warning">
                <LuAlertCircle style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} size={18} />
                {errorMessage || 'Nossa equipe já foi avisada e vai entrar em contato para resolver. Se preferir, fale com a gente agora pelo WhatsApp.'}
              </Alert>

              <WhatsAppSupportLink
                href={getSupportWhatsAppLink('confirmacao')}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaWhatsapp size={22} />
                Falar com o atendimento
              </WhatsAppSupportLink>

              <Button onClick={() => navigate('/')} $variant="secondary">
                <LuArrowLeft style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} size={18} />
                Voltar para a Home
              </Button>
            </CardBody>
          </Card>
        </Container>
        <Footer />
      </PageWrapper>
    );
  }

  // ESTADO: AGUARDANDO CPF (Validação de identidade + Finalização)
  if (status === 'awaiting_cpf' || status === 'finalizing') {
    return (
      <PageWrapper>
        <Header />
        <Container>
          <PageTitle>
            <h1>Confirmação de Cadastro</h1>
            <p>Valide sua identidade para continuar</p>
          </PageTitle>
          <Card>
            <CardHeader $status="brand">
              <IconWrapper $status="brand">
                <LuUser size={40} />
              </IconWrapper>
              <Title>Confirme seu CPF</Title>
              <Subtitle>Só para garantir que é você quem está finalizando.</Subtitle>
            </CardHeader>
            <CardBody>
              <Alert $type="warning">
                <LuAlertCircle style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} size={18} />
                Informe o CPF do titular da contratação para concluir com segurança.
              </Alert>

              <InfoSection>
                <InfoTitle>
                  <LuUser style={{ marginRight: '8px' }} /> Dados do Cadastro
                </InfoTitle>
                <InfoItem>
                  <InfoLabel>Nome:</InfoLabel>
                  <InfoValue>{cadastroData?.nome || 'Não informado'}</InfoValue>
                </InfoItem>
                {cadastroData?.email && (
                  <InfoItem>
                    <InfoLabel>E-mail:</InfoLabel>
                    <InfoValue>{cadastroData.email}</InfoValue>
                  </InfoItem>
                )}
              </InfoSection>

              <FormGroup>
                <Label htmlFor="cpf">
                  <LuKey style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} size={16} />
                  CPF do Titular <span style={{ color: '#ef4444' }}>*</span>
                </Label>
                <Input
                  id="cpf"
                  type="text"
                  placeholder="000.000.000-00"
                  value={cpf}
                  onChange={handleCPFChange}
                  maxLength="14"
                  $error={!!cpfError}
                  disabled={status === 'finalizing'}
                />
                {cpfError && (
                  <ErrorText>
                    <LuAlertCircle size={14} />
                    {cpfError}
                  </ErrorText>
                )}
              </FormGroup>

              <Button 
                onClick={handleFinalizarCadastro}
                disabled={status === 'finalizing' || !cpf}
                $mb="12px"
              >
                {status === 'finalizing' ? (
                  <>
                    <SpinningIcon size={18} />
                    Finalizando Cadastro...
                  </>
                ) : (
                  <>
                    <LuCheck style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} size={18} />
                    Finalizar Cadastro
                  </>
                )}
              </Button>
              
              <Button onClick={() => navigate('/')} $variant="secondary">
                <LuArrowLeft style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} size={18} />
                Cancelar
              </Button>
            </CardBody>
          </Card>
        </Container>
        <Footer />
      </PageWrapper>
    );
  }

  // ESTADO: SUCCESS (Cadastro finalizado)
  if (status === 'success' && dadosCompletos) {
    // Validar se os dados essenciais existem
    const hasPortalData = dadosCompletos.portal?.login && dadosCompletos.portal?.contrato;
    const hasPixData = dadosCompletos.pix?.qrCode && dadosCompletos.pix?.copiaCola;
    
    // Se não tiver dados essenciais, mostrar erro
    if (!hasPortalData || !hasPixData) {
      return (
        <PageWrapper>
          <Header />
          <Container>
            <Card>
              <CardHeader $status="error">
                <IconWrapper $status="error">
                  <LuAlertCircle size={48} />
                </IconWrapper>
                <Title>Faltam alguns dados</Title>
                <Subtitle>Não conseguimos carregar todas as informações do seu cadastro.</Subtitle>
              </CardHeader>
              <CardBody>
                <Alert $type="error">
                  <LuAlertCircle style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} size={18} />
                  Fale com o nosso atendimento informando o ID do cadastro: <strong>{dadosCompletos.cadastroId || 'N/A'}</strong>
                </Alert>
                
                {!hasPortalData && (
                  <Alert $type="warning" style={{ marginTop: '15px' }}>
                    <LuAlertTriangle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />
                    Dados do portal não foram retornados pela API
                  </Alert>
                )}

                {!hasPixData && (
                  <Alert $type="warning" style={{ marginTop: '15px' }}>
                    <LuAlertTriangle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />
                    Dados do PIX não foram retornados pela API
                  </Alert>
                )}

                <Button onClick={() => navigate('/')} $variant="secondary" style={{ marginTop: '20px' }}>
                  <LuArrowLeft style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} size={18} />
                  Voltar para Home
                </Button>
              </CardBody>
            </Card>
          </Container>
          <Footer />
        </PageWrapper>
      );
    }
    
    // Se tudo OK, mostrar os dados
    return (
      <PageWrapper>
        <Header />
        <Container>
          <PageTitle>
            <h1>Cadastro finalizado</h1>
            <p>Bem-vindo à Santa Casa Copacabana</p>
          </PageTitle>
          <Card>
            <CardHeader $status="success">
              <IconWrapper $status="success">
                <LuCheckCircle size={48} />
              </IconWrapper>
              <Title>Cadastro finalizado</Title>
              <Subtitle>
                Falta só o pagamento. Escolha PIX ou cartão abaixo — seus dados de acesso ao portal ficam no final da página.
              </Subtitle>
            </CardHeader>
            <CardBody>

              <MobilePaymentSelector>
                <MobilePaymentButton
                  type="button"
                  onClick={() => setSelectedPaymentMethod('pix')}
                  $active={selectedPaymentMethod === 'pix'}
                >
                  <LuSmartphone size={16} />
                  Pagar com PIX
                </MobilePaymentButton>
                <MobilePaymentButton
                  type="button"
                  onClick={() => setSelectedPaymentMethod('card')}
                  $active={selectedPaymentMethod === 'card'}
                >
                  <LuCreditCard size={16} />
                  Pagar com Cartão
                </MobilePaymentButton>
              </MobilePaymentSelector>

              {/* Área de Pagamento: PIX | CARTÃO DE CRÉDITO */}
              <PaymentWrapper>
                {/* Coluna PIX */}
                <PixSection
                  style={
                    isMobile && selectedPaymentMethod !== 'pix'
                      ? { display: 'none' }
                      : undefined
                  }
                >
                  <PixTitle>
                    <LuSmartphone style={{ marginRight: '8px' }} /> Pagamento via PIX
                  </PixTitle>
                  
                  <QRCode src={dadosCompletos.pix.qrCode} alt="QR Code PIX" />
                  
                  <p style={{ textAlign: 'center', fontSize: '13px', color: '#065f46', marginBottom: '12px' }}>
                    Escaneie o QR Code ou copie o código abaixo:
                  </p>
                  
                  <PixCodeBox>{dadosCompletos.pix.copiaCola}</PixCodeBox>
                  
                  <CopyButton onClick={copyPixCode}>
                    {copySuccess ? (
                      <>
                        <LuCheck style={{ marginRight: '8px' }} size={18} />
                        Copiado!
                      </>
                    ) : (
                      <>
                        <LuCopy style={{ marginRight: '8px' }} size={18} />
                        Copiar Código PIX
                      </>
                    )}
                  </CopyButton>
                  
                  {/* Informação sobre PIX no portal */}
                  <div style={{
                    marginTop: '15px',
                    background: '#f1f5f9',
                    color: '#334155',
                    padding: '15px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    borderLeft: '3px solid #1e3a8a'
                  }}>
                    <LuLink style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} size={16} />
                    <strong>Dica:</strong> Caso não consiga realizar o pagamento agora, o código PIX também está disponível no portal do cliente.
                  </div>
                </PixSection>

                {/* Coluna Cartão de Crédito */}
                <CardPaymentSection
                  style={
                    isMobile
                      ? selectedPaymentMethod === 'card'
                        ? { display: 'flex' }
                        : { display: 'none' }
                      : undefined
                  }
                >
                  <div>
                    <CardPaymentTitle>
                      <LuCreditCard style={{ marginRight: '8px' }} /> Cartão de Crédito
                    </CardPaymentTitle>
                    <CardPaymentText>
                      O pagamento via cartão de crédito vale apenas para a primeira mensalidade. As demais parcelas
                      poderão ser pagas via boleto ou PIX diretamente no portal do cliente.
                    </CardPaymentText>
                    <CardPaymentText>
                      Ao clicar em &quot;Pagar com Cartão de Crédito&quot; você será redirecionado para o link do nosso
                      parceiro para finalizar o pagamento em um ambiente seguro.
                    </CardPaymentText>
                    {cardLinkError && (
                      <Alert $type="error" style={{ marginBottom: '12px' }}>
                        <LuAlertCircle style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} size={14} />
                        {cardLinkError}
                      </Alert>
                    )}
                  </div>
                  <CardPaymentButton onClick={handlePagarComCartao} disabled={isGeneratingCardLink}>
                    {isGeneratingCardLink ? (
                      <>
                        <SpinningIcon size={16} />
                        Gerando link de pagamento...
                      </>
                    ) : (
                      <>
                        <LuCreditCard size={18} />
                        Pagar com Cartão de Crédito
                      </>
                    )}
                  </CardPaymentButton>
                </CardPaymentSection>
              </PaymentWrapper>

              <Alert $type="warning" style={{ marginTop: '16px' }}>
                <LuAlertCircle style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} size={18} />
                <strong>Importante:</strong> A confirmação do pagamento não é exibida nesta tela. Após pagar via PIX ou
                Cartão de Crédito, você poderá acompanhar a baixa e as próximas parcelas diretamente pelo portal do
                cliente.
              </Alert>

              {/* Credenciais do Portal - MOVIDO PARA BAIXO DO PIX */}
              <PortalSection>
                <PortalTitle>
                  <LuKey style={{ marginRight: '8px' }} /> Acesso ao Portal
                </PortalTitle>
                <PortalInfo>
                  <PortalLabel>Login (CPF):</PortalLabel>
                  <PortalValue>{dadosCompletos.portal.login}</PortalValue>
                </PortalInfo>
                {dadosCompletos.portal.contrato && (
                  <PortalInfo>
                    <PortalLabel>Contrato:</PortalLabel>
                    <PortalValue>{dadosCompletos.portal.contrato}</PortalValue>
                  </PortalInfo>
                )}
                <PortalLink href={dadosCompletos.portal.url || 'https://portal.santacasacopacabana.com.br/'} target="_blank" rel="noopener noreferrer">
                  <LuLink style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} size={18} />
                  Acessar Portal
                </PortalLink>
              </PortalSection>

              {/* Aviso de Expiração - ATUALIZADO */}
              <Alert $type="warning" style={{ marginTop: '20px' }}>
                <LuAlertTriangle style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} size={18} />
                <strong>Importante:</strong> Caso o pagamento não seja identificado até a data do vencimento, 
                seu cadastro será cancelado automaticamente.
              </Alert>

              <Button onClick={() => navigate('/')} $variant="secondary" style={{ marginTop: '20px' }}>
                <LuArrowLeft style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} size={18} />
                Voltar para Home
              </Button>
            </CardBody>
          </Card>
        </Container>
        <Footer />
      </PageWrapper>
    );
  }

  return null;
};

export default ConfirmacaoCadastro;
